import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, XCircle, Compass, Utensils, Trash2, RotateCcw } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const fmtISK = (n) => `${Math.round(n || 0).toLocaleString("en-US").replace(/,/g, ".")} ISK`;
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return d; }
};
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function RefundsCancellations() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all"); // all | tour | dinner

  const [missing, setMissing] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/refunds-cancellations")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load refunds"))
      .finally(() => setLoading(false));
    api.get("/admin/restore-purged-refunds").then((r) => setMissing(r.data)).catch(() => {});
  }, []);

  const restore = () => {
    if (!window.confirm(`Recreate ${missing.count} refunded booking(s) from the Activity Log?\n\nThey come back marked Refunded / Cancelled — visible in Tour Bookings, Dinner bookings and reports, but never counted as revenue. No emails are sent.`)) return;
    setRestoring(true);
    api.post("/admin/restore-purged-refunds")
      .then((r) => { toast.success(`Restored ${r.data.restored} refunded booking(s)`); load(); })
      .catch(() => toast.error("Restore failed"))
      .finally(() => setRestoring(false));
  };
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    fetch(`${API}/admin/refunds-cancellations/export.csv`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("immersion_admin_token")}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "refunds-cancellations.csv";
        a.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  const refunded = (data?.refunded || []).filter((r) => kind === "all" || r.kind === kind);
  const purges = data?.purges || [];

  return (
    <div data-testid="refunds-page" className="min-h-screen bg-black text-white">
      <div className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center gap-4">
        <Link to="/admin" data-testid="back-to-admin" className="text-gray-400 hover:text-white inline-flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Dashboard</Link>
        <span className="font-display font-bold tracking-tight text-lg ml-2">Refunds &amp; Cancellations</span>
        <div className="ml-auto flex items-center gap-3">
          <button data-testid="export-refunds-csv" onClick={exportCsv} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-2.5 px-5 transition-colors inline-flex items-center gap-2"><Download size={14} /> CSV</button>
          <button data-testid="refunds-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="text-gray-500 hover:text-white text-sm uppercase tracking-[0.15em]">Logout</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 px-1 py-10"><Loader2 size={18} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            {missing?.count > 0 && (
              <div data-testid="restore-refunds-banner" className="border border-amber-300/40 bg-amber-300/[0.06] p-5 mb-8 flex items-start gap-4 flex-wrap">
                <RotateCcw size={20} className="text-amber-300 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-[240px]">
                  <p className="font-semibold text-sm">{missing.count} refunded booking(s) were removed by an earlier purge</p>
                  <p className="text-xs text-gray-400 mt-1">Found in your Activity Log ({fmtISK(missing.total_amount)} refunded): {missing.candidates.map((c) => `${c.guest_name} · ${c.kind === "dinner" ? "Dinner" : c.name}`).join(", ")}</p>
                </div>
                <button data-testid="restore-refunds-btn" onClick={restore} disabled={restoring}
                  className="bg-amber-300 text-black hover:bg-amber-200 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 disabled:opacity-50">
                  {restoring ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Restore as refunded
                </button>
              </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
              <div className="border border-white/10 bg-[#0A0A0C] p-5" data-testid="summary-count">
                <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500">Refunded / Cancelled</div>
                <div className="font-display font-extrabold text-3xl mt-2 text-[#C44536]">{data.total_refunded_count}</div>
              </div>
              <div className="border border-white/10 bg-[#0A0A0C] p-5" data-testid="summary-amount">
                <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500">Total refunded</div>
                <div className="font-display font-extrabold text-2xl mt-2">{fmtISK(data.total_refunded_amount)}</div>
              </div>
              <div className="border border-white/10 bg-[#0A0A0C] p-5" data-testid="summary-purged">
                <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500">Purged carts</div>
                <div className="font-display font-extrabold text-3xl mt-2 text-gray-400">{data.total_purged}</div>
              </div>
            </div>

            {/* Refunded / cancelled list */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <XCircle size={18} className="text-[#C44536]" />
              <h2 className="font-display font-bold uppercase tracking-tight text-lg">Refunded &amp; Cancelled bookings</h2>
              <div className="ml-auto flex gap-2">
                {[["all", "All"], ["tour", "Tours"], ["dinner", "Dinners"]].map(([k, l]) => (
                  <button key={k} data-testid={`filter-${k}`} onClick={() => setKind(k)}
                    className={`px-3.5 py-2 border text-[11px] uppercase tracking-[0.12em] font-bold transition-colors ${kind === k ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="border border-white/10 bg-[#0A0A0C] mb-12" data-testid="refunded-list">
              {refunded.length === 0 ? (
                <p className="px-5 py-12 text-center text-gray-600 text-sm">No refunded or cancelled bookings on file for this filter.</p>
              ) : (
                refunded.map((r) => (
                  <div key={`${r.kind}-${r.id}`} data-testid={`refund-row-${r.id}`} className="flex items-start gap-4 px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                    <div className={`shrink-0 mt-0.5 ${r.kind === "dinner" ? "text-amber-300" : "text-[#8CA8B9]"}`}>
                      {r.kind === "dinner" ? <Utensils size={18} /> : <Compass size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#C44536]">
                          {r.payment_status === "refunded" ? "Refunded" : "Cancelled"}
                        </span>
                        <span className="text-[11px] text-gray-600">·</span>
                        <span className="text-[11px] text-gray-500 uppercase tracking-[0.1em]">{r.kind}</span>
                        {r.restored && <span data-testid={`restored-badge-${r.id}`} className="text-[10px] uppercase tracking-[0.12em] font-bold text-amber-300 border border-amber-300/40 px-1.5 py-0.5">Restored from history</span>}
                      </div>
                      <p className="text-white text-sm mt-1">{r.label}{r.seating ? ` · ${r.seating}` : ""}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {r.guest_name}{r.guest_email ? ` · ${r.guest_email}` : ""} · {fmtDate(r.date)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-bold">{fmtISK(r.amount)}</p>
                      {r.refunded_at && <p className="text-[11px] text-gray-600 mt-1">{fmtTime(r.refunded_at)}</p>}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Purge history */}
            <div className="flex items-center gap-2 mb-4">
              <Trash2 size={18} className="text-gray-400" />
              <h2 className="font-display font-bold uppercase tracking-tight text-lg">Purged abandoned carts</h2>
            </div>
            <div className="border border-white/10 bg-[#0A0A0C]" data-testid="purge-list">
              {purges.length === 0 ? (
                <p className="px-5 py-12 text-center text-gray-600 text-sm">No purges recorded yet. Only abandoned / unpaid carts are ever purged — paid &amp; refunded bookings are always kept.</p>
              ) : (
                purges.map((p) => (
                  <div key={p.id} data-testid={`purge-row-${p.id}`} className="px-5 py-4 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm font-semibold">{p.deleted_count} cart(s) removed</span>
                      <span className="text-[11px] text-gray-600">·</span>
                      <span className="text-[11px] text-gray-500">{p.actor}</span>
                      <span className="text-[11px] text-gray-600 ml-auto">{fmtTime(p.ts)}</span>
                    </div>
                    {p.ids?.length > 0 && (
                      <p className="text-[11px] text-gray-500 mt-2 break-words font-mono">{p.ids.join("  ·  ")}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
